// frontend/src/features/livestock/SpeciesAutocomplete.tsx

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  TextInput,
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Loader,
  UnstyledButton,
  Box,
  Divider,
  Select,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconPlus, IconFilter } from '@tabler/icons-react';
import { fishSpeciesApi } from '@/api/fishSpecies';
import type { FishSpeciesSearchResult } from '@/types/livestock';

interface SpeciesAutocompleteProps {
  onSelect: (species: FishSpeciesSearchResult | { common_name: string; scientific_name: null; category: string | null }) => void;
  placeholder?: string;
  initialValue?: string;
  showCategoryFilter?: boolean;
}

export function SpeciesAutocomplete({
  onSelect,
  placeholder = 'Search for species...',
  initialValue = '',
  showCategoryFilter = true,
}: SpeciesAutocompleteProps) {
  const [query, setQuery] = useState(initialValue);
  const [debouncedQuery] = useDebouncedValue(query, 200);
  const [results, setResults] = useState<FishSpeciesSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load categories on mount
  useEffect(() => {
    if (showCategoryFilter) {
      setCategoriesLoading(true);
      fishSpeciesApi.getCategories()
        .then((response) => {
          // Extract category names from response objects
          setCategories(response.categories.map(c => c.name));
        })
        .catch((err) => {
          console.error('Failed to load categories:', err);
        })
        .finally(() => {
          setCategoriesLoading(false);
        });
    }
  }, [showCategoryFilter]);

  const searchSpecies = useCallback(async (searchQuery: string, category: string | null) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fishSpeciesApi.search(searchQuery, 8, category || undefined);
      setResults(response.results);
      setIsOpen(true);
    } catch (err) {
      console.error('Species search failed:', err);
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update query when initialValue changes (e.g., when modal opens with pre-filled search)
  useEffect(() => {
    if (initialValue) {
      setQuery(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (debouncedQuery) {
      searchSpecies(debouncedQuery, selectedCategory);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [debouncedQuery, selectedCategory, searchSpecies]);

  const handleSelect = useCallback((species: FishSpeciesSearchResult) => {
    onSelect(species);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, [onSelect]);

  const handleCustomEntry = useCallback(() => {
    onSelect({ common_name: query, scientific_name: null, category: selectedCategory });
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, [onSelect, query, selectedCategory]);

  const getCareColor = (level: string | null) => {
    if (!level) return 'gray';
    switch (level.toLowerCase()) {
      case 'easy': return 'green';
      case 'moderate': return 'yellow';
      case 'difficult': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Stack gap="xs">
      {showCategoryFilter && (
        <Select
          placeholder="Filter by category"
          data={categories.map((cat) => ({ value: cat, label: cat }))}
          value={selectedCategory}
          onChange={setSelectedCategory}
          clearable
          searchable
          leftSection={<IconFilter size={16} />}
          disabled={categoriesLoading}
          size="sm"
        />
      )}
      <Box style={{ position: 'relative' }}>
        <TextInput
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={placeholder}
          leftSection={<IconSearch size={16} />}
          rightSection={isLoading ? <Loader size="xs" /> : null}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          error={error}
          maxLength={255}
        />

        {isOpen && (query.length >= 2) && (
          <Paper
            shadow="md"
            withBorder
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            <Stack gap={0}>
              {isLoading ? (
                <Group p="md" justify="center">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">Searching...</Text>
                </Group>
              ) : results.length > 0 ? (
                <>
                  {results.map((species) => (
                    <UnstyledButton
                      key={species.id}
                      onClick={() => handleSelect(species)}
                      p="sm"
                      style={{
                        display: 'block',
                        width: '100%',
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Box>
                          <Group gap="xs">
                            <Text fw={500}>{species.common_name}</Text>
                            {species.category && (
                              <Badge size="xs" variant="light" color="blue">
                                {species.category}
                              </Badge>
                            )}
                          </Group>
                          {species.scientific_name && (
                            <Text size="sm" c="dimmed" fs="italic">
                              {species.scientific_name}
                            </Text>
                          )}
                        </Box>
                        <Group gap="xs">
                          {species.care_level && (
                            <Badge size="xs" color={getCareColor(species.care_level)}>
                              {species.care_level}
                            </Badge>
                          )}
                          {species.max_size_inches && (
                            <Text size="xs" c="dimmed">{species.max_size_inches}" max</Text>
                          )}
                        </Group>
                      </Group>
                    </UnstyledButton>
                  ))}
                </>
              ) : (
                <Text p="md" size="sm" c="dimmed" ta="center">
                  No species found
                </Text>
              )}

              <Divider />
              <UnstyledButton
                onClick={handleCustomEntry}
                p="sm"
                style={{
                  display: 'block',
                  width: '100%',
                }}
              >
                <Group gap="xs">
                  <IconPlus size={16} />
                  <Text size="sm">Add "{query}" as custom entry</Text>
                </Group>
              </UnstyledButton>
            </Stack>
          </Paper>
        )}
      </Box>
    </Stack>
  );
}
